import { Box, Flex, Text } from '@chakra-ui/react'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useAgents } from '../../hooks/agents/useAgents'
import { useLanguage } from '../../hooks/utility/useLanguage'
import { AnsweredBy, InternetSearchDecision, KnowledgeSource, MessageThread, UploadedAsset } from '../../types/chatTypes'
import { headerAuthors, threadAuthors } from '../../utils/threadUtil'
import { AGENT_COMMAND } from '../../core/parsers/slashCommandParser'
import { ParsedCommand } from '../../types/commandTypes'
import { AIModel, ModelStatus } from '../../types/modelTypes'
import ChatBubble from '../ChatBubble'
import { ClarificationForm } from '../ClarificationForm'
import { QuestionInput } from '../QuestionInput'
import { AgentWatermark } from './AgentWatermark'

// Pixels of empty space left above the freshly-sent message when it anchors to
// the top of the scroll viewport, so it doesn't sit flush under the chat header.
// Applied by stopping the scroll short rather than padding the thread: the
// message already carries a top margin that collapses to just above the anchor,
// so stopping short reveals a clean slice of that empty margin. Padding the
// thread instead would break the margin collapse and jump the gap by the full
// margin (~20px) the moment it's non-zero, leaving no fine control.
const SCROLL_ANCHOR_TOP_GAP = 12

interface ChatInterfaceProps {
  conversation: MessageThread[]
  chatContainerRef: React.RefObject<HTMLDivElement>
  isInputDisabled: boolean
  isTyping: boolean // To pass to QuestionInput or for other UI elements if needed
  onSend: (message: string, attachedDocuments?: UploadedAsset[], commands?: ParsedCommand[]) => void
  onRetry: (question: string, tempId: string, searchKnowledgeSource?: KnowledgeSource) => void
  // Answers a pending clarification: the Q:/A: text is sent as the next message.
  onSubmitClarification?: (threadTempId: string, qaString: string) => void
  onTypingChange: (isTyping: boolean) => void
  hasDocumentsInConversation: boolean // Prop to indicate if conversation contains documents
  onToggleResponseMinimized?: (threadTempId: string, responseMessageId: string) => void
  currentPhase?: { tempId: string; phase: 'internal_streaming' | 'llm_resumed' } | null
  isStreaming?: boolean // Track if any response is currently streaming
  onStop?: () => void // Abort the in-flight response (Stop button)
  models?: AIModel[]
  modelStatus?: Record<string, ModelStatus>
  selectedModel?: string
  onSelectModel?: (model: string | undefined) => void
  conversationId?: string
  onResolveInternetSearch?: (tempId: string, decision: InternetSearchDecision, queries?: (string | null)[]) => void
}

export const ChatInterface: React.FC<ChatInterfaceProps> = ({
  conversation,
  chatContainerRef,
  isInputDisabled,
  onSend,
  onRetry,
  onSubmitClarification,
  onTypingChange,
  hasDocumentsInConversation,
  onToggleResponseMinimized,
  currentPhase,
  isStreaming,
  onStop,
  models,
  modelStatus,
  selectedModel,
  onSelectModel,
  conversationId,
  onResolveInternetSearch,
}) => {
  const { t } = useLanguage()
  const { currentAgent } = useAgents()
  const authors = threadAuthors(conversation)
  // Only a specialist is labelled; the conversation's own agent needs none.
  const specialist = (author?: AnsweredBy) =>
    author && currentAgent && author.id !== currentAgent.id ? author : undefined
  // The header also names the own agent; `author` stays specialist-only for the ask-again menu.
  const headers = headerAuthors(
    conversation,
    currentAgent ?? undefined,
    !!isStreaming,
    currentAgent?.type === 'privileged',
  )
  // Follow-ups go to whoever finished the last answer, as on the server. While
  // an `@` pick or a leave still streams, nobody is shown.
  const finishedAuthors = threadAuthors(conversation, true)
  const lastThread = conversation[conversation.length - 1]
  const handedOff =
    !!isStreaming &&
    !!lastThread &&
    !finishedAuthors[finishedAuthors.length - 1] &&
    !!lastThread.appliedCommands?.some(c => c.name === AGENT_COMMAND)
  const activeAgent = handedOff ? undefined : specialist([...finishedAuthors].reverse().find(author => author))

  // Attached to the most recent thread so a freshly submitted question can be
  // scrolled to the top of the viewport.
  const lastThreadRef = useRef<HTMLDivElement | null>(null)
  // Armed on send/retry, consumed once when the conversation actually grows.
  const shouldScrollOnGrowthRef = useRef(false)
  const previousConversationLengthRef = useRef(conversation.length)
  // Viewport height of the scroll container, used to reserve a full screen for
  // the active thread so its question can reach the top.
  const [viewportHeight, setViewportHeight] = useState(0)

  useEffect(() => {
    const container = chatContainerRef.current
    if (!container) return

    const updateViewportHeight = () => setViewportHeight(container.clientHeight)
    updateViewportHeight()

    const resizeObserver = new ResizeObserver(updateViewportHeight)
    resizeObserver.observe(container)
    return () => resizeObserver.disconnect()
  }, [chatContainerRef])

  // One-shot scroll: when the conversation grows after a send/retry, bring the
  // new question to the top. Requiring actual growth (not just the armed flag)
  // makes this robust against the retry path, which first removes the old
  // thread and then re-adds it, transiently changing the array.
  useEffect(() => {
    const conversationGrew = conversation.length > previousConversationLengthRef.current
    previousConversationLengthRef.current = conversation.length

    if (!shouldScrollOnGrowthRef.current || !conversationGrew) return

    const container = chatContainerRef.current
    const lastThread = lastThreadRef.current
    if (!container || !lastThread) return

    // Scroll the container itself rather than using scrollIntoView, which would
    // also scroll overflow:hidden ancestors and shift the entire layout.
    const offsetFromContainerTop = lastThread.getBoundingClientRect().top - container.getBoundingClientRect().top
    container.scrollTo({
      top: container.scrollTop + offsetFromContainerTop - SCROLL_ANCHOR_TOP_GAP,
      behavior: 'smooth',
    })
    shouldScrollOnGrowthRef.current = false
  }, [conversation.length, chatContainerRef])

  // The most recent unanswered clarification, if any. The card floats above
  // the composer rather than inside the bubble, so it sits where the user
  // would otherwise type the answer.
  const activeClarification = useMemo(() => {
    for (let i = conversation.length - 1; i >= 0; i--) {
      const thread = conversation[i]
      for (const answer of thread.answers) {
        if (answer.clarification) {
          return {
            tempId: thread.tempId,
            questions: answer.clarification.questions,
            messageId: answer.clarification.message_id,
          }
        }
      }
    }
    return null
  }, [conversation])

  // Handler for sending messages that includes the current tool
  const handleSendMessage = (message: string, attachedDocuments?: UploadedAsset[], commands?: ParsedCommand[]) => {
    shouldScrollOnGrowthRef.current = true
    onSend(message, attachedDocuments, commands)
  }

  // Handler for retrying messages that includes the current tool
  const handleRetry = (question: string, tempId: string) => {
    shouldScrollOnGrowthRef.current = true
    onRetry(question, tempId)
  }

  // The same question, its directives kept, to the assistant picked under an answer.
  const handleAskAgain = (thread: MessageThread, pick: string) => {
    if (!thread.question) return
    const kept = (thread.appliedCommands ?? []).filter(command => command.name !== AGENT_COMMAND)
    handleSendMessage(thread.question, undefined, [...kept, { name: AGENT_COMMAND, args: [pick] }])
  }

  // Handler for example question clicks
  const handleExampleQuestionClick = (question: string) => {
    handleSendMessage(question)
  }

  return (
    <Flex h='100%' flexDir='column' bg='chat.canvas'>
      <Flex
        ref={chatContainerRef}
        flex={1}
        minHeight='0'
        overflowY='scroll'
        overflowX='hidden'
        flexDirection='column'
      >
        <Box px={{ base: 4, md: 6 }} w='100%' maxW='816px' mx='auto' pt={0} pb={6} h='100%'>
          {conversation.length === 0 && currentAgent ? (
            <AgentWatermark agent={currentAgent} onQuestionClick={handleExampleQuestionClick} />
          ) : (
            conversation.map((thread, index) => {
              const isLastThread = index === conversation.length - 1
              return (
                <Box
                  key={thread.tempId}
                  ref={isLastThread ? lastThreadRef : undefined}
                  minHeight={isLastThread && viewportHeight ? `${viewportHeight}px` : undefined}
                >
                  <ChatBubble
                    thread={thread}
                    onRetry={() => {
                      if (thread.question) {
                        handleRetry(thread.question, thread.tempId)
                      }
                    }}
                    onToggleResponseMinimized={
                      onToggleResponseMinimized
                        ? responseMessageId => onToggleResponseMinimized(thread.tempId, responseMessageId)
                        : undefined
                    }
                    currentPhase={currentPhase}
                    onResolveInternetSearch={
                      onResolveInternetSearch
                        ? (decision, queries) => onResolveInternetSearch(thread.tempId, decision, queries)
                        : undefined
                    }
                    author={specialist(authors[index])}
                    headerAuthor={headers[index]}
                    // Only the latest: an older question asked again lands at the bottom,
                    // with everything said after it in the new agent's memory.
                    onAskAgain={isLastThread ? pick => handleAskAgain(thread, pick) : undefined}
                    isAskAgainDisabled={isInputDisabled}
                  />
                </Box>
              )
            })
          )}
        </Box>
      </Flex>
      <Box
        display='flex'
        flexDirection='column'
        position='sticky'
        bottom='0'
        py={2}
        bg='chat.canvas'
        px={{ base: 4, md: 6 }}
      >
        {activeClarification && onSubmitClarification && (
          <Box display='flex' justifyContent='center' mb={3}>
            <Box w='100%' maxW='768px'>
              <ClarificationForm
                key={activeClarification.messageId}
                questions={activeClarification.questions}
                onSubmit={qaString => {
                  shouldScrollOnGrowthRef.current = true
                  onSubmitClarification(activeClarification.tempId, qaString)
                }}
              />
            </Box>
          </Box>
        )}
        <Box display='flex' justifyContent='center'>
          <Box w='100%' maxW='768px'>
            <Box>
              <QuestionInput
                key={`${currentAgent?.id ?? ''}:${conversationId ?? ''}`}
                clearOnSend
                placeholder={t('chat.placeholder')}
                disabled={isInputDisabled}
                onTyping={() => onTypingChange(true)}
                onStopTyping={() => onTypingChange(false)}
                onSend={handleSendMessage}
                hasDocumentsInConversation={hasDocumentsInConversation}
                isStreaming={isStreaming}
                onStop={onStop}
                models={models}
                modelStatus={modelStatus}
                selectedModel={selectedModel}
                onSelectModel={onSelectModel}
                conversationId={conversationId}
                activeAgent={activeAgent}
              />
            </Box>
          </Box>
        </Box>
        <Box pb={4} />
        <Text fontSize='xs' color='chat.muted' textAlign='center'>
          {t('chat.disclaimerText')}
        </Text>
      </Box>
    </Flex>
  )
}
